/**
 * Port number validation and formatting.
 * For gateways 1 and 2, old format (1A, 2B) is allowed; otherwise ROW.SLOT (e.g. 1.01).
 */

const LEGACY_PORT_FORMAT_GATEWAY_IDS = [1, 2];

/**
 * @param {string} portNumber
 * @param {Array} existingPorts
 * @param {number|null} excludePortId
 * @param {number|null} gatewayId - If 1 or 2, old format (1A, 2B) is valid
 * @returns {{ valid: boolean, error: string|null }}
 */
export function validatePortNumber(portNumber, existingPorts = [], excludePortId = null, gatewayId = null) {
  if (!portNumber || !portNumber.trim()) {
    return { valid: false, error: 'Port number is required' };
  }

  const trimmed = portNumber.trim();
  const useLegacy = gatewayId != null && LEGACY_PORT_FORMAT_GATEWAY_IDS.includes(Number(gatewayId));

  if (trimmed.includes('_')) {
    return { valid: false, error: 'Invalid character "_" in port number. Use digits only (e.g., 1.01)' };
  }

  // Old format (1A, 2B) for gateways 1 and 2
  if (useLegacy && /^\d+[A-Ha-h]$/.test(trimmed)) {
    const row = parseInt(trimmed.match(/^\d+/)[0], 10);
    if (row >= 1 && row <= 16) {
      const existing = existingPorts.find(
        (p) => p.port_number === trimmed && (!excludePortId || p.id !== excludePortId)
      );
      if (existing) {
        return { valid: false, error: `Port ${trimmed} already exists in this gateway` };
      }
      return { valid: true, error: null };
    }
  }

  if (!/^[\d.]+$/.test(trimmed)) {
    return { valid: false, error: 'Port number must contain only digits and one dot (e.g., 1.01)' };
  }

  const formatMatch = trimmed.match(/^(\d+)\.(\d+)$/);
  if (!formatMatch) {
    return { valid: false, error: 'Invalid format. Use format: ROW.SLOT (e.g., 1.01, 16.32)' };
  }

  const row = parseInt(formatMatch[1], 10);
  const slot = parseInt(formatMatch[2], 10);

  if (row < 1 || row > 16) {
    return { valid: false, error: 'Row must be between 1 and 16' };
  }
  if (slot < 1 || slot > 32) {
    return { valid: false, error: 'Slot must be between 01 and 32' };
  }

  const existing = existingPorts.find(
    (p) => p.port_number === trimmed && (!excludePortId || p.id !== excludePortId)
  );
  if (existing) {
    return { valid: false, error: `Port ${trimmed} already exists in this gateway` };
  }

  return { valid: true, error: null };
}

/**
 * Clean input: remove invalid chars, keep digits and one dot.
 * @param {string} input
 * @returns {string}
 */
export function cleanPortNumberInput(input) {
  if (!input) return '';

  let cleaned = input.replace(/[^\d.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  if (cleaned.includes('.')) {
    const [row, slot] = cleaned.split('.');
    if (slot && slot.length === 1) {
      cleaned = `${row}.0${slot}`;
    }
  }
  return cleaned;
}

/**
 * Format for display (e.g. 1.1 -> 1.01). Invalid returns "—" or "⚠️ ...".
 * @param {string} portNumber
 * @returns {string}
 */
export function formatPortNumber(portNumber) {
  if (!portNumber) return '—';
  if (/^\d+\.\d{2}$/.test(portNumber)) return portNumber;
  const match = portNumber.match(/^(\d+)\.(\d+)$/);
  if (match) {
    return `${match[1]}.${match[2].padStart(2, '0')}`;
  }
  if (/^\d+[A-Ha-h]$/.test(portNumber)) return portNumber;
  return `⚠️ ${portNumber}`;
}

/**
 * @param {string} portNumber
 * @returns {boolean}
 */
export function isOldFormat(portNumber) {
  if (!portNumber) return false;
  return /^\d+[A-Ha-h]$/.test(portNumber);
}

/**
 * Convert old format to new (1A -> 1.01). Returns null if invalid.
 * @param {string} oldPortNumber
 * @returns {string|null}
 */
export function convertOldToNewFormat(oldPortNumber) {
  if (!oldPortNumber) return null;
  const match = oldPortNumber.match(/^(\d+)([A-Ha-h])$/);
  if (!match) return null;
  const oldRow = parseInt(match[1], 10);
  const letter = match[2].toUpperCase();
  const slotMap = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8 };
  const slot = slotMap[letter];
  if (slot == null || oldRow < 1 || oldRow > 16) return null;
  return `${oldRow}.${String(slot).padStart(2, '0')}`;
}
